'use client'

import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useDepositCryptoSchema } from '@repo/shared/schemas'

import {
  Card,
  Col,
  Alert,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  SelectModal,
  SelectModalContent,
  SelectModalItems,
  SelectModalTrigger,
  SelectModalValue,
  Typography,
  Row,
  IconNames,
  Button,
  AspectRatio,
} from '@repo/ui/components'
import { useIsDesktop } from '@repo/ui/hooks'
import { DepositCryptoCTA } from './deposit-crypto.cta'

import { CryptoOutputItem } from './crypto.output-item'
import { CryptoInstantPrice } from './crypto-instant-price'
import { useMemo, useState } from 'react'
import { cn } from '@repo/ui/lib'
import { ValueWithUnit, globalConfig } from '@repo/shared/index'
import {
  useInitiateCryptoDepositInput,
  useListProjects,
  useUserGetAvailableCryptoContracts,
} from '@repo/services'
import Image from 'next/image'

type CryptoNetwork = {
  id: number
  name: string
  faName: string
  symbol: string
  icon: React.ReactNode
  estimatedTime: number
}
type CryptoToken = {
  id: number
  name: string
  faName: string
  symbol: string
  icon: React.ReactNode
  networks: CryptoNetwork[]
  price: number
}

type CryptoOutput = {
  title: string
  value: string
  isCopyable: boolean
  networkName?: string
  networkBadge?: string
  networkIcon?: React.ReactNode
}
export const DepositCryptoContainer = () => {
  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()
  const { data: availableCryptoContracts } = useUserGetAvailableCryptoContracts(
    { direction: 'asc' },
  )

  const { data: projectsData } = useListProjects({
    skip: 0,
    limit: 10,
    kind: 'crypto',
  })
  const tokensData =
    projectsData?.pages.flatMap((page) => page.data?.projects || []) || []

  const tokens = (availableCryptoContracts?.data?.contracts || []).map(
    (contract, index) => {
      return {
        id: index,
        name: contract.currency,
        faName: contract.currency,
        symbol: contract.currency,
        icon: tokensData.find((t) => t.token === contract.currency)?.logoURL ? (
          <AspectRatio className='aspect-1/1'>
            <Image
              src={`${globalConfig.baseImageUrl}${tokensData.find((t) => t.token === contract.currency)?.logoURL}`}
              alt={contract.currency || 'Crypto Token'}
              fill
              sizes='(max-width: 768px) 100vw, 36px'
              className='h-full w-full  rounded-2xs object-cover'
              loading='lazy'
            />
          </AspectRatio>
        ) : (
          <Icon name='CardLine' size='sm' />
        ),
        networks: (contract.blockchains || [])?.map(
          (blockchain, indexNetwork) => {
            return {
              id: index * 10 + indexNetwork,
              name: blockchain.name,
              faName: blockchain.name,
              symbol: blockchain.name,
              icon: blockchain.name ? (
                <Icon name={blockchain.name as IconNames} size='sm' />
              ) : (
                <Icon name='CardLine' size='sm' />
              ),
              estimatedTime: 10,
            }
          },
        ),
        price:
          tokensData.find((t) => t.token === contract.currency)?.price
            ?.priceRange24h?.last || 0,
      }
    },
  ) as CryptoToken[]

  const { schema } = useDepositCryptoSchema()
  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      token: undefined,
      blockchain: undefined,
    },
  })

  const initiateCryptoDepositInputMutation = useInitiateCryptoDepositInput({
    invalidate: true,
  })

  const isDesktop = useIsDesktop()

  const selectedToken = form.watch('token')
  const selectedBlockchain = form.watch('blockchain')

  const [outputItems, setOutputItems] = useState<CryptoOutput[]>([])

  const onSubmit = (data: FormSchema<typeof schema>) => {
    initiateCryptoDepositInputMutation.mutate(
      {
        token: data.token,
        blockchain: data.blockchain,
      },
      {
        onSuccess: (data) => {
          const project = tokensData.find((t) => t.token === selectedToken)
          setOutputItems([
            {
              title: t('depositCryptoPage_select_address', {
                token: selectedToken?.name,
                network: selectedBlockchain,
              }),
              value: data.data?.address ?? '',
              isCopyable: true,
              networkName: selectedBlockchain?.name,
              networkBadge: selectedBlockchain?.name,
              networkIcon: project?.token ? (
                <AspectRatio className='aspect-1/1'>
                  <Image
                    src={
                      project?.logoURL
                        ? `${globalConfig.baseImageUrl}${project?.logoURL}`
                        : '/images/placeholder.webp'
                    }
                    alt='Photo of property'
                    fill
                    sizes='(max-width: 768px) 100vw, 24px'
                    className='h-full w-full  rounded-2xs object-cover'
                    loading='lazy'
                  />
                </AspectRatio>
              ) : (
                <Icon name='CardLine' size='sm' />
              ),
            },
            // {
            //   title: t('depositCryptoPage_memo'),
            //   value: data.data?.destinationTag ?? '',
            //   isCopyable: true,
            // },
          ])
        },
        onError: errorHandler,
      },
    )
  }

  const networks = useMemo(() => {
    return selectedToken
      ? (tokens.find((t) => t.symbol === selectedToken)
          ?.networks as CryptoNetwork[])
      : ([] as CryptoNetwork[])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToken])

  const CryptoSelectItem = ({
    selected,
    children,
  }: {
    selected?: boolean
    children: React.ReactNode
  }) => {
    return (
      <Row
        className={cn(
          'justify-between items-center w-full p-sm rounded-sm',
          selected && 'bg-neutral-surface-20',
        )}
      >
        {children}
      </Row>
    )
  }

  const CryptoSelectValue = ({
    value,
    placeholder,
  }: {
    value?: string
    placeholder?: string
  }) => {
    const token = tokens.find((t) => t.symbol === value)
    const network = networks.find((n) => n.symbol === value)
    const item = token || network

    if (!value || !item) {
      return <span className='text-neutral-surface-60'>{placeholder}</span>
    }

    return (
      <Row className='gap-xs items-center'>
        <span className='size-4xl rounded-full bg-base-surface-10 p-xs flex items-center justify-center rounded-full'>
          {item.icon}
        </span>
        <Typography size='md' color='text-neutral-surface-100'>
          {item.name}
        </Typography>
      </Row>
    )
  }

  return (
    <Card className='max-w-container-md mx-auto p-xl' withoutBorder>
      {isDesktop && <DepositCryptoCTA origin='container' />}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} id='deposit-crypto-form'>
          <Col className='gap-3xl'>
            <Alert
              color='alert'
              size='md'
              icon={<Icon name='AlertTriangleLine' size='sm' />}
              caption={t('depositCryptoPage_hint')}
            />

            <CryptoInstantPrice />

            <FormField
              control={form.control}
              name='token'
              render={({ field }) => (
                <SelectModal {...field} onValueChange={field.onChange}>
                  <FormItemWrapper
                    title={t('depositCryptoPage_select_token')}
                    inputSize='md'
                  >
                    <SelectModalTrigger inputSize='md' className='w-full'>
                      <SelectModalValue
                        placeholder={t(
                          'depositCryptoPage_select_token_placeholder',
                        )}
                        customValue={CryptoSelectValue}
                      />
                    </SelectModalTrigger>
                  </FormItemWrapper>
                  <SelectModalContent
                    title={t('depositCryptoPage_select_token')}
                    backButtonLabel={t('common_back')}
                    searchPlaceholder={t('common_search')}
                  >
                    <Row>
                      <Typography size='xs' color='text-neutral-surface-80'>
                        {t('depositCryptoPage_select_token_all')}
                      </Typography>
                    </Row>
                    <SelectModalItems
                      items={tokens}
                      getValue={(item, idx) => item.symbol || String(idx)}
                      renderItem={(item) => {
                        const displayName = item.faName
                        return (
                          <>
                            <Row className='gap-sm items-center'>
                              <div className='bg-base-surface-10 rounded-full p-xs flex items-center justify-center size-4xl rounded-full'>
                                {item.icon}
                              </div>
                              <Col className='gap-2xs'>
                                <Typography
                                  size='md'
                                  weight='regular'
                                  color='text-neutral-surface-100'
                                >
                                  {displayName}
                                </Typography>
                                <Typography
                                  size='sm'
                                  color='text-neutral-surface-60'
                                >
                                  {item.symbol}
                                </Typography>
                              </Col>
                            </Row>
                            <Row className='gap-xs items-end justify-end'>
                              <ValueWithUnit
                                value={item?.price || 0}
                                scale={10}
                                unit={'TMN'}
                                valueSize='md'
                                valueWeight='regular'
                                valueColor='text-neutral-surface-100'
                              />
                            </Row>
                          </>
                        )
                      }}
                      customItem={CryptoSelectItem}
                      filterFn={(item, searchTerm) => {
                        const lowerSearchTerm = searchTerm.toLowerCase()
                        return (
                          (item?.name
                            ?.toLowerCase()
                            .includes(lowerSearchTerm) ||
                            item?.faName
                              ?.toLowerCase()
                              .includes(lowerSearchTerm) ||
                            item?.symbol
                              ?.toLowerCase()
                              .includes(lowerSearchTerm)) ??
                          false
                        )
                      }}
                    />
                  </SelectModalContent>
                </SelectModal>
              )}
            />
            <FormField
              control={form.control}
              name='blockchain'
              render={({ field }) => (
                <SelectModal {...field} onValueChange={field.onChange}>
                  <FormItemWrapper
                    title={t('depositCryptoPage_select_network')}
                    inputSize='md'
                  >
                    <SelectModalTrigger inputSize='md' className='w-full'>
                      <SelectModalValue
                        placeholder={t(
                          'depositCryptoPage_select_network_placeholder',
                        )}
                        customValue={CryptoSelectValue}
                      />
                    </SelectModalTrigger>
                  </FormItemWrapper>
                  <SelectModalContent
                    title={t('depositCryptoPage_select_network')}
                    backButtonLabel={t('common_back')}
                    searchPlaceholder={t('common_search')}
                  >
                    <Alert
                      color='alert'
                      size='md'
                      icon={<Icon name='AlertTriangleLine' size='sm' />}
                      caption={t('depositCryptoPage_hint_network')}
                      className='mb-md'
                    />
                    <Row className='gap-xs items-center justify-between'>
                      <Typography size='xs' color='text-neutral-surface-80'>
                        {t('depositCryptoPage_select_network_all')}
                      </Typography>
                      <Typography size='xs' color='text-neutral-surface-80'>
                        {t('depositCryptoPage_select_network_estimated_time')}
                      </Typography>
                    </Row>
                    <SelectModalItems
                      items={networks}
                      getValue={(item, idx) => item.symbol || String(idx)}
                      renderItem={(item) => (
                        <>
                          <Row className='gap-sm items-center'>
                            <div className='bg-base-surface-10 rounded-full p-xs flex items-center justify-center size-3xl'>
                              {item.icon}
                            </div>
                            <Row className='gap-2xs items-center w-fit'>
                              <Typography
                                size='sm'
                                color='text-neutral-surface-60'
                              >
                                {item.symbol}
                              </Typography>
                              -
                              <Typography
                                size='md'
                                weight='regular'
                                color='text-neutral-surface-100'
                              >
                                {item.faName}
                              </Typography>
                            </Row>
                          </Row>
                          <Row className='gap-xs items-end justify-end'>
                            {item.estimatedTime}{' '}
                            {t(
                              'depositCryptoPage_select_network_estimated_time_unit',
                            )}
                          </Row>
                        </>
                      )}
                      customItem={CryptoSelectItem}
                      filterFn={(item, searchTerm) => {
                        const lowerSearchTerm = searchTerm.toLowerCase()
                        return (
                          item.name.toLowerCase().includes(lowerSearchTerm) ||
                          item.faName.toLowerCase().includes(lowerSearchTerm) ||
                          item.symbol.toLowerCase().includes(lowerSearchTerm)
                        )
                      }}
                    />
                  </SelectModalContent>
                </SelectModal>
              )}
            />

            <Button
              size='lg'
              color='base'
              className='w-full mt-lg'
              type='submit'
              loading={initiateCryptoDepositInputMutation.isPending}
              startIcon={<Icon name='WalletLine' size='sm' />}
            >
              {t('depositCryptoPage_create_address')}
            </Button>

            {outputItems.map((item) => (
              <CryptoOutputItem key={item.title} {...item} />
            ))}
          </Col>
        </form>
      </Form>
    </Card>
  )
}
